import { useNotification } from "rc-notification";
import { FC, PropsWithChildren, createContext, useCallback, useMemo } from "react";
import SiteNotification, {
    ISiteNotificationVariant,
} from "components/base/notification/SiteNotification";

export type INoticeEventVariant = "default" | "modal";

export interface INoticeEvent {
    type: "error" | "info" | "warning";
    title: string;
    message: string;
}

type INotificationContext = {
    notice: (event: INoticeEvent, variant?: ISiteNotificationVariant, onClose?: () => void) => void;
};

export const NotificationContext = createContext<INotificationContext>(null as any);

export const NotificationProvider: FC<PropsWithChildren> = ({ children }) => {
    const [instance, holder] = useNotification({
        maxCount: 3,
    });

    const notice = useCallback(
        (
            event: INoticeEvent,
            variant: ISiteNotificationVariant = "default",
            onClose?: () => void,
        ) => {
            instance?.open({
                className: `notification-${variant}`,
                key: event.title,
                content: (
                    <SiteNotification
                        variant={variant}
                        type={event.type}
                        title={event.title}
                        message={event.message}
                        instance={instance}
                    />
                ),
                duration: variant === "modal" ? 60 * 3 : 3,
                closable: true,
                onClose: () => {
                    if (onClose) {
                        onClose();
                    }
                },
                style:
                    variant === "modal"
                        ? {
                              top: "50%",
                              left: "50%",
                              transform: "translate(-50%,-50%)",
                              position: "fixed",
                              zIndex: 10,
                              width: "100%",
                          }
                        : {
                              bottom: 60,
                              right: 0,
                              position: "fixed",
                              zIndex: 10,
                          },
            });
        },
        [instance],
    );

    const value = useMemo(() => ({ notice }), [notice]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
            {holder}
        </NotificationContext.Provider>
    );
};
